import React, { useMemo, useState } from "react";
import { Box, Button, Sheet, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import { AddIcon } from "static/image/common/IconAssets";
import type { PublishedTaskEditorTask } from "./utils/taskEditorRuntime";
import { buildTaskEditorListSections } from "./utils/taskEditorListUtils";
import { UpArrowSvg } from "../../static/image/common/SvgAssets";

interface TaskListPanelProps {
  tasks: PublishedTaskEditorTask[];
  activeTaskId?: string;
  onSelectTask: (taskId: string) => void;
  onCreateTask: () => void;
  onEditMeta: (taskId: string) => void;
}

const PANEL_WIDTH = "160px";
const KEY_BADGE_COLOR = "#F59E0B";

export default function TaskListPanel({
  tasks,
  activeTaskId,
  onSelectTask,
  onCreateTask,
  onEditMeta,
}: TaskListPanelProps) {
  const sections = useMemo(() => buildTaskEditorListSections(tasks), [tasks]);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set()
  );

  const toggleSection = (sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        borderRadius: 0,
        backgroundColor: theme.vars.palette.Surface.Forward,
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
        }}
      ></Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          py: "4px",
        }}
      >
        <Button
          size="sm"
          variant="Plain"
          startDecorator={<AddIcon size={12}></AddIcon>}
          onClick={onCreateTask}
          sx={{
            width: "100%",
            height: "12px",
            justifyContent: "flex-start",
            fontSize: "12px",
            fontWeight: 500,
          }}
        >
          새 활동지 추가
        </Button>

        {sections.length === 0 && (
          <Box sx={{ p: "16px", textAlign: "center" }}>
            <Typography
              level="body-xs"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              활동지 없음
            </Typography>
          </Box>
        )}

        {sections.map((section) => {
          const isCollapsed = collapsedSections.has(section.key);
          return (
            <Box key={section.key} sx={{ mb: "2px" }}>
              <Box
                onClick={() => toggleSection(section.key)}
                sx={{
                  px: "8px",
                  py: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  userSelect: "none",
                  "&:hover": {
                    backgroundColor: theme.vars.palette.Background.Level1,
                  },
                }}
              >
                <Box
                  sx={{
                    ...(isCollapsed && {
                      transform: "rotate(-90deg)",
                      transformOrigin: "center",
                    }),
                    transition: "transform 0.2s",
                  }}
                >
                  <UpArrowSvg
                    color={theme.vars.palette.Content.Weak}
                    size={16}
                  />
                </Box>
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 700,
                    color: theme.vars.palette.Content.Weak,
                    fontSize: "11px",
                    letterSpacing: "0.02em",
                  }}
                >
                  {section.label}
                </Typography>
              </Box>

              {!isCollapsed &&
                section.tasks.map((task) => {
                  const isActive = activeTaskId === task.taskId;
                  return (
                    <Box
                      key={task.taskId}
                      onClick={() => onSelectTask(task.taskId)}
                      sx={{
                        pl: "16px",
                        pr: "8px",
                        py: "4px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        borderLeft: `2px solid ${
                          isActive
                            ? theme.vars.palette.Content.Strong
                            : "transparent"
                        }`,
                        backgroundColor: isActive
                          ? theme.vars.palette.Background.Level2
                          : "transparent",
                        "& .meta-edit-btn": {
                          visibility: "hidden",
                        },
                        "&:hover": {
                          backgroundColor: isActive
                            ? theme.vars.palette.Background.Level2
                            : theme.vars.palette.Background.Level1,
                        },
                        "&:hover .meta-edit-btn": {
                          visibility: "visible",
                        },
                      }}
                    >
                      <Typography
                        level="body-xs"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "12px",
                          fontWeight: isActive ? 600 : 400,
                          color: theme.vars.palette.Content.Strong,
                        }}
                        title={`${task.title || "(제목 없음)"}`}
                      >
                        {task.title || "(제목 없음)"}
                      </Typography>
                      {task.isKeyActivity && (
                        <Typography
                          level="body-xs"
                          sx={{
                            fontSize: "11px",
                            color: KEY_BADGE_COLOR,
                            flexShrink: 0,
                            lineHeight: 1,
                          }}
                          title="Key 활동지"
                        >
                          ★
                        </Typography>
                      )}
                      <Box
                        className="meta-edit-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditMeta(task.taskId);
                        }}
                        sx={{
                          flexShrink: 0,
                          fontSize: "14px",
                          lineHeight: 1,
                          color: theme.vars.palette.Content.Weak,
                          cursor: "pointer",
                          px: "2px",
                          "&:hover": {
                            color: theme.vars.palette.Content.Strong,
                          },
                        }}
                        title="메타데이터 편집"
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{
                            fontSize: "16px",
                          }}
                        >
                          more_vert
                        </span>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          );
        })}
      </Box>
    </Sheet>
  );
}
